import FormSection from 'components/FormSection';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import Divider from 'components/Divider';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getAccessParentAccounts } from 'api/accessexpressv3/api';

import { InformationCircle } from 'react-ionicons';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { getInvoiceStyleOptions } from 'screens/LetUsHelp/GetStarted/CreateChildAccount/utils';
import {
  billinTypeOptions,
  invoiceDeliveryOptions,
} from 'shared/constants/options';
import { useAccessCheckoutContext } from '../context/AccessCheckoutProvider';
import { Controller, useFieldArray } from 'react-hook-form';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';
import { observer } from 'mobx-react-lite';
import { SearchAccessAddressBar } from '../BaseComponents/Cart/Locations/SearchAccessAddressBar';
import { hasAnyNetworkProduct, hasOnlyPrimaryCellularProducts } from '../utils';
import { useCartContext } from '../CartProvider';
import { ShippingPreferences } from './ShippingPreferences';

const Checkout = observer(() => {
  const cart = useCartContext();
  const { form, currentIndex, onSubmit, locations } =
    useAccessCheckoutContext();

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    watch,
    resetField,
    setValue,
  } = form;

  const [currentCity, currentMacnum, currentBillingType] = watch([
    `locations.${currentIndex}.city`,
    `locations.${currentIndex}.macnum`,
    `locations.${currentIndex}.billing_type`,
    `locations.${currentIndex}.invoice_style`,
    `locations.${currentIndex}.invoice_delivery_method`,
    `locations.${currentIndex}.parent_account`,
  ]);
  const needToCreateAccount = useMemo(
    () => !currentMacnum && !!currentCity,
    [currentCity, currentMacnum],
  );
  const [parentAccountOptions, setParentAccountOptions] = useState<
    { label: string; value: string; subvalue?: string }[]
  >([]);

  useQuery(['access-parent-accounts'], () => getAccessParentAccounts(), {
    enabled: needToCreateAccount,
    onSuccess: (data) => {
      setParentAccountOptions(
        data.map((item) => {
          const parts = item.name
            ? item.name.split('-').map((part) => part.trim())
            : ['', ''];
          // need to handle this part!!
          return {
            label: parts[0],
            value: parts[0],
            subvalue: parts[1],
          };
        }),
      );
    },
  });
  const { fields } = useFieldArray({ control, name: 'locations' });

  const currentLocation = useMemo(
    () => locations[currentIndex],
    [currentIndex, locations],
  );
  const items =
    currentLocation?.id !== undefined
      ? cart.primaryItemsByLocation(currentLocation.id)
      : cart.unassignedPrimaryMobilityItems;

  const containsNetworkProducts = hasAnyNetworkProduct(items);
  const containsOnlyPrimaryCellularProducts =
    hasOnlyPrimaryCellularProducts(items);

  return (
    <>
      <form
        id="access-checkout-form"
        onSubmit={handleSubmit(onSubmit)}
        className="rounded bg-gradient-to-b from-[rgba(71,71,104,0.08)] to-[rgba(51,51,80,0.08)] p-6"
      >
        {fields.map((field, index) => {
          if (currentIndex !== index) return null;
          return (
            <div key={field.id} className="flex flex-col gap-8">
              <FormSection title="Service address" gridClassName="grid-cols-1">
                <div className="col-span-1">
                  <GraniteLabel label="Address" />
                  <Controller
                    control={control}
                    name={`locations.${currentIndex}.city`}
                    render={() => (
                      <SearchAccessAddressBar
                        value={
                          form.getValues().locations[currentIndex]?.city
                            ? {
                                ...form.getValues().locations[currentIndex],
                                id: null,
                              }
                            : undefined
                        }
                        onAddressSelected={(address) => {
                          const prefix = `locations.${currentIndex}` as const;
                          if (address) {
                            setValue(`${prefix}.macnum`, address.macnum);
                            setValue(
                              `${prefix}.address_line_1`,
                              address.address_line_1 ?? '',
                            );
                            setValue(
                              `${prefix}.address_line_2`,
                              address.address_line_2 ?? '',
                            );
                            setValue(`${prefix}.street`, address.street ?? '');
                            setValue(`${prefix}.city`, address.city ?? '');
                            setValue(`${prefix}.state`, address.state ?? '');
                            setValue(`${prefix}.zip`, address.zip ?? '');
                            setValue(`${prefix}.id`, String(address.id ?? ''));
                            setValue(`${prefix}.name`, address.name);
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-expect-error
                            setValue(`${prefix}.site_name`, address.site_name);
                            setValue(
                              `${prefix}.parent_macnum`,
                              address.parent_macnum,
                            );
                          } else {
                            setValue(`${prefix}.macnum`, '');
                            setValue(`${prefix}.address_line_1`, '');
                            setValue(`${prefix}.address_line_2`, '');
                            setValue(`${prefix}.street`, '');
                            setValue(`${prefix}.city`, '');
                            setValue(`${prefix}.state`, '');
                            setValue(`${prefix}.zip`, '');
                            setValue(`${prefix}.id`, '');
                            setValue(`${prefix}.name`, '');
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-expect-error
                            setValue(`${prefix}.site_name`, '');
                            setValue(`${prefix}.parent_macnum`, '');
                            setValue(`${prefix}.parent_account`, undefined);
                            setValue(`${prefix}.location_name`, '');
                            setValue(`${prefix}.billing_type`, undefined);
                            setValue(
                              `${prefix}.invoice_delivery_method`,
                              undefined,
                            );
                            setValue(`${prefix}.invoice_style`, undefined);
                          }
                        }}
                        name="search address"
                        error={
                          errors.locations?.[currentIndex]?.location_name &&
                          !currentCity
                            ? 'Required'
                            : undefined
                        }
                        className="col-span-2"
                        variant="blue"
                      />
                    )}
                  />
                </div>
                {needToCreateAccount && (
                  <>
                    <div className="flex items-center gap-2 rounded bg-[#9796F314] p-4 ring-1 ring-purple-300">
                      <InformationCircle color={'rgba(151, 150, 243, 1)'} />
                      <p className="font-bold text-content-base-default">
                        This address is not associated with an existing account.
                        Create a new account by completing the below fields.
                      </p>
                    </div>
                    <FormSection gridClassName="relative grid-cols-1 md:grid-cols-2">
                      <GraniteInput
                        label="Location name"
                        variant="blue"
                        className="col-span-1"
                        innerInputClassName="!w-full !box-border"
                        {...register(`locations.${index}.location_name`)}
                        error={
                          errors.locations?.[index]?.location_name?.message
                        }
                      />
                      <Controller
                        {...register(`locations.${index}.parent_account`)}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <GraniteSelect
                            {...field}
                            options={parentAccountOptions}
                            label="Parent account"
                            onChange={onChange}
                            value={value}
                            error={
                              errors.locations?.[index]?.parent_account?.message
                            }
                            className="col-span-1"
                            variant="blue"
                          />
                        )}
                      />
                    </FormSection>
                    <FormSection gridClassName="relative grid-cols-1 md:grid-cols-3">
                      <Controller
                        {...register(`locations.${index}.billing_type`)}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <GraniteSelect
                            {...field}
                            options={billinTypeOptions}
                            label="Billing type"
                            isSearchable={false}
                            onChange={(selectedOption: OptionType | null) => {
                              onChange(selectedOption);
                              if (selectedOption) {
                                resetField(`locations.${index}.invoice_style`);
                              }
                            }}
                            value={value}
                            error={
                              errors.locations?.[index]?.billing_type?.message
                            }
                            className="col-span-1"
                            variant="blue"
                          />
                        )}
                      />
                      <Controller
                        {...register(`locations.${index}.invoice_style`)}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <GraniteSelect
                            {...field}
                            options={
                              currentBillingType
                                ? getInvoiceStyleOptions(currentBillingType)
                                : []
                            }
                            isSearchable={false}
                            label="Invoice style"
                            onChange={onChange}
                            value={value || null}
                            error={
                              errors.locations?.[index]?.invoice_style?.message
                            }
                            className="col-span-1"
                            variant="blue"
                          />
                        )}
                      />

                      <Controller
                        {...register(
                          `locations.${index}.invoice_delivery_method`,
                        )}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <GraniteSelect
                            {...field}
                            isSearchable={false}
                            options={invoiceDeliveryOptions}
                            label="Invoice delivery method"
                            onChange={onChange}
                            value={value}
                            error={
                              errors.locations?.[index]?.invoice_delivery_method
                                ?.message
                            }
                            className="col-span-1"
                            variant="blue"
                          />
                        )}
                      />
                    </FormSection>
                  </>
                )}
              </FormSection>
              <FormSection gridClassName="relative grid-cols-1 md:grid-cols-2">
                <GraniteInput
                  {...register(`locations.${currentIndex}.business_name`)}
                  label="Business name"
                  variant="blue"
                  className="col-span-1"
                  innerInputClassName="!w-full !box-border"
                  error={
                    errors.locations?.[currentIndex]?.business_name?.message
                  }
                />
                <GraniteInput
                  {...register(`locations.${currentIndex}.local_contact_name`)}
                  label="Name"
                  error={
                    errors.locations?.[currentIndex]?.local_contact_name
                      ?.message
                  }
                  variant="blue"
                  className="col-span-1"
                  innerInputClassName="!w-full !box-border"
                />
                <GraniteInput
                  {...register(
                    `locations.${currentIndex}.location_contact_email`,
                  )}
                  label="Email"
                  placeholder="email@example.com"
                  className="col-span-1"
                  variant="blue"
                  error={
                    errors.locations?.[currentIndex]?.location_contact_email
                      ?.message
                  }
                />
                <PhoneNumberInput
                  name={`locations.${currentIndex}.local_contact_phone`}
                  control={control}
                  label="Phone number"
                  variant="blue"
                  className="col-span-1"
                  innerInputClassName="!w-full !box-border"
                  error={
                    errors.locations?.[currentIndex]?.local_contact_phone
                      ?.message
                  }
                />
              </FormSection>
              <Divider />
              <ShippingPreferences
                control={control}
                errors={errors}
                currentIndex={currentIndex}
                containsOnlyPrimaryCellularProducts={
                  containsOnlyPrimaryCellularProducts
                }
                containsNetworkProducts={containsNetworkProducts}
              />
            </div>
          );
        })}
      </form>
    </>
  );
});

export default Checkout;
